import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatAmountInWords } from '../../../utils/amountInWords';

const CashMemoDirectPreview = ({ data, isLoading }) => {
    const [searchParams] = useSearchParams();
    const preview = searchParams.get('preview');
    const [showPrintModal, setShowPrintModal] = useState(false);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const formatCurrency = (amount) => {
        if (amount === undefined || amount === null || amount === '') return '0.00';
        return parseFloat(amount).toFixed(2);
    };

    // Safely extract translated/text values from possible objects like { en: '...' }
    const getText = (value, preferredLang = 'en') => {
        if (!value) return '';
        if (typeof value === 'string') return value;
        if (typeof value === 'object') {
            if (value[preferredLang]) return value[preferredLang];
            const first = Object.values(value)[0];
            return typeof first === 'string' ? first : '';
        }
        return String(value);
    };

    useEffect(() => {
        if (!data) return;
        if (preview) setShowPrintModal(true);
    }, [preview, data]);

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="text-center p-4">
                <h5>No data available</h5>
            </div>
        );
    }

    const handlePrint = () => {
        setShowPrintModal(true);
    };

    const handleClosePrintModal = () => {
        setShowPrintModal(false);
    };

    return (
        <div className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between mb-2">
                <div className="d-flex justify-content-start">
                    <h5 className="mb-0">Cash Memo Direct Details</h5>
                </div>
                <div className="d-flex justify-content-end">
                    <button 
                        className="btn btn-primary btn-sm no-print"
                        onClick={handlePrint}
                        style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                    >
                        <i className="fas fa-print"></i>
                        Print Preview
                    </button>
                </div>
            </div>

            <div className="flex-grow-1">
                <div className="row">
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Voucher Number:</label>
                            <div className="form-control-plaintext">{data.voucher_number || data.number || 'N/A'}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Voucher Date:</label>
                            <div className="form-control-plaintext">{formatDate(data.voucher_date || data.date)}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Customer:</label>
                            <div className="form-control-plaintext">{getText(data.customer?.label) || getText(data.customer_name) || 'N/A'}</div>
                        </div>
                    </div>
                </div>

                <div className="table-responsive mt-4">
                    <table className="table table-bordered">
                        <thead>
                            <tr>
                                <th className="text-nowrap p-3">Item Code</th>
                                <th className="text-nowrap p-3">Item Name</th>
                                <th className="text-nowrap p-3">Unit</th>
                                <th className="text-nowrap p-3">Qty</th>
                                <th className="text-nowrap p-3">Unit Price</th>
                                <th className="text-nowrap p-3">Discount %</th>
                                <th className="text-nowrap p-3">Line Amount</th>
                                <th className="text-nowrap p-3">VAT %</th>
                                <th className="text-nowrap p-3">VAT Amt</th>
                                <th className="text-nowrap p-3">Amt Incl VAT</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items && data.items.length > 0 ? (
                                data.items.map((item, index) => (
                                    <tr key={index}>
                                        <td className="text-nowrap p-3 align-middle">{getText(item.item?.item_code) || 'N/A'}</td>
                                        <td className="text-nowrap p-3 align-middle">{getText(item.item?.label) || getText(item.item_name) || 'N/A'}</td>
                                        <td className="p-3 align-middle">{getText(item.unit) || 'N/A'}</td>
                                        <td className="p-3 align-middle">{item.quantity || '0'}</td>
                                        <td className="p-3 align-middle">{formatCurrency(item.unit_price)}</td>
                                        <td className="p-3 align-middle">{item.discount_percentage || '0.00'}%</td>
                                        <td className="p-3 align-middle">{formatCurrency(item.line_amount)}</td>
                                        <td className="p-3 align-middle">{item.vat_percentage || '0.00'}%</td>
                                        <td className="p-3 align-middle">{formatCurrency(item.vat_amount)}</td>
                                        <td className="p-3 align-middle">{formatCurrency(item.amount_incl_vat)}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="10" className="text-center">No items added</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="row mt-4 d-flex justify-content-end">
                    <div className="col-md-4">
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Gross Total:</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{formatCurrency(data.gross_total)}</div>
                            </div>
                        </div>
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Discount %:</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{data.discount_percentage || '0.00'}%</div>
                            </div>
                        </div>
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Discount Amt:</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{formatCurrency(data.discount_amount)}</div>
                            </div>
                        </div>
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Sub Total:</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{formatCurrency(data.sub_total)}</div>
                            </div>
                        </div>
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">VAT Amt:</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{formatCurrency(data.vat_amount)}</div>
                            </div>
                        </div>
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Net Total:</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{formatCurrency(data.net_total)}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showPrintModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div style={{ backgroundColor: 'white', width: '90%', height: '90%', borderRadius: '8px', position: 'relative', overflow: 'hidden' }}>
                        <div style={{ padding: '15px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h5 style={{ margin: 0 }}>Print Preview</h5>
                            <div>
                                <button className="btn btn-primary btn-sm me-2" onClick={() => {
                                    const printWindow = window.open('', '_blank');
                                    printWindow.document.write(`
                                        <html>
                                            <head>
                                                <title>Cash Memo Direct Print</title>
                                                <style>
                                                    body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; line-height: 1.4; }
                                                    @media print { body { margin: 0; } }
                                                    table { width: 100%; border-collapse: collapse; }
                                                    th, td { padding: 6px; }
                                                    .bordered th, .bordered td { border: 1px solid #ddd; }
                                                </style>
                                            </head>
                                            <body>
                                                <div id="print-content"></div>
                                            </body>
                                        </html>
                                    `);
                                    const modalContent = document.querySelector('.modal-print-content-cashmemodirect');
                                    if (modalContent) {
                                        const printContent = modalContent.innerHTML;
                                        printWindow.document.getElementById('print-content').innerHTML = printContent;
                                    }
                                    printWindow.document.close();
                                    setTimeout(() => { printWindow.print(); printWindow.close(); }, 100);
                                }}>
                                    <i className="fas fa-print me-1"></i>
                                    Print
                                </button>
                                <button className="btn btn-secondary btn-sm" onClick={handleClosePrintModal}>
                                    <i className="fas fa-times me-1"></i>
                                    Close
                                </button>
                            </div>
                        </div>
                        <div style={{ height: 'calc(100% - 70px)', overflow: 'auto', padding: '20px' }} className="modal-print-content-cashmemodirect">
                            {/* Tax Invoice styled printable content */}
                            <div style={{ fontFamily: 'Arial, sans-serif', color: '#000' }}>
                                {/* Header */}
                                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '8px' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ width: '33%', verticalAlign: 'top', fontSize: '11px', lineHeight: 1.4 }}>
                                                <div>Tel :</div>
                                                <div>Show Room : {data.company?.showroom_phone || ''}</div>
                                                <div>Office : {data.company?.office_phone || ''}</div>
                                                <div>Fax : {data.company?.fax || ''}</div>
                                                <div>E-mail : {data.company?.email || ''}</div>
                                                <div>{data.company?.building || ''}</div>
                                                <div>{data.company?.address_line || ''}</div>
                                                <div>P.O. Box : {data.company?.po_box || ''}</div>
                                                <div>Kingdom of Bahrain</div>
                                                <div>C.R. No.: {data.company?.cr_no || ''}</div>
                                            </td>
                                            <td style={{ width: '34%', textAlign: 'center', verticalAlign: 'top' }}>
                                                <div style={{ display: 'inline-block' }}>
                                                    <img src={`${process.env.PUBLIC_URL}/logo-sm.svg`} alt="logo" style={{ height: '48px', marginBottom: '6px' }} />
                                                </div>
                                                <div style={{ fontWeight: 700, fontSize: '18px', letterSpacing: '1px' }}>{data.company?.name || 'ALSHAKAR'}</div>
                                                <div style={{ fontSize: '11px', letterSpacing: '1px' }}>TAILORING W.L.L</div>
                                            </td>
                                            <td style={{ width: '33%', verticalAlign: 'top', fontSize: '11px', lineHeight: 1.4, textAlign: 'right' }}>
                                                <div>تلفون :</div>
                                                <div>{data.company?.ar_showroom_phone || ''}</div>
                                                <div>{data.company?.ar_office_phone || ''}</div>
                                                <div>{data.company?.ar_fax || ''}</div>
                                                <div>{data.company?.ar_email || ''}</div>
                                                <div>{data.company?.ar_address_line || ''}</div>
                                                <div style={{ marginTop: '6px' }}>VAT No. {data.company?.vat_no || ''}</div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                {/* Title */}
                                <div style={{ textAlign: 'center', fontWeight: 700, textDecoration: 'underline', margin: '6px 0 10px' }}>TAX INVOICE</div>

                                {/* Customer / Invoice blocks */}
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginBottom: '8px' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ verticalAlign: 'top', width: '60%' }}>
                                                <div style={{ fontWeight: 700, marginBottom: '4px' }}>{getText(data.customer_code) || getText(data.customer?.code) || ''}</div>
                                                <div style={{ fontWeight: 700 }}>{getText(data.customer_name) || getText(data.customer?.label) || ''}</div>
                                                <div>{getText(data.address1) || ''}</div>
                                                <div>{getText(data.address2) || ''}</div>
                                                <div>{getText(data.country?.label) || getText(data.country?.name) || 'KINGDOM OF BAHRAIN'}</div>
                                                <div style={{ display: 'flex', gap: '12px', marginTop: '6px' }}>
                                                    <span>PHONE: {getText(data.phone_number) || ''}</span>
                                                    <span>VAT No: {getText(data.vat_number) || getText(data.customer?.vat_number) || ''}</span>
                                                </div>
                                            </td>
                                            <td style={{ verticalAlign: 'top', width: '40%' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                    <tbody>
                                                        <tr>
                                                            <td style={{ padding: '3px 6px', fontWeight: 700 }}>VOUCHER NO:</td>
                                                            <td style={{ padding: '3px 6px', textAlign: 'right' }}>{getText(data.voucher_number) || getText(data.number) || ''}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ padding: '3px 6px', fontWeight: 700 }}>VOUCHER DATE:</td>
                                                            <td style={{ padding: '3px 6px', textAlign: 'right' }}>{formatDate(data.voucher_date || data.date)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ padding: '3px 6px', fontWeight: 700 }}>LPO NO:</td>
                                                            <td style={{ padding: '3px 6px', textAlign: 'right' }}>{getText(data.lpo_number) || '-'}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ padding: '3px 6px', fontWeight: 700 }}>LPO DATE:</td>
                                                            <td style={{ padding: '3px 6px', textAlign: 'right' }}>{formatDate(data.lpo_date)}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                {/* Items table */}
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ borderTop: '1px solid #000', borderBottom: '1px solid #000', textAlign: 'left', padding: '6px' }}>ITEM</th>
                                            <th style={{ borderTop: '1px solid #000', borderBottom: '1px solid #000', textAlign: 'center', padding: '6px', width: '70px' }}>UNIT</th>
                                            <th style={{ borderTop: '1px solid #000', borderBottom: '1px solid #000', textAlign: 'center', padding: '6px', width: '90px' }}>QUANTITY</th>
                                            <th style={{ borderTop: '1px solid #000', borderBottom: '1px solid #000', textAlign: 'right', padding: '6px', width: '90px' }}>PRICE</th>
                                            <th style={{ borderTop: '1px solid #000', borderBottom: '1px solid #000', textAlign: 'right', padding: '6px', width: '120px' }}>AMT. EXCL. VAT</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(data.items || []).map((item, idx) => (
                                            <tr key={idx}>
                                                <td style={{ padding: '6px', borderBottom: '1px solid #eee' }}>{getText(item.item?.label) || getText(item.item?.name) || getText(item.item_description) || 'N/A'}</td>
                                                <td style={{ padding: '6px', textAlign: 'center', borderBottom: '1px solid #eee' }}>{getText(item.unit) || 'PCS'}</td>
                                                <td style={{ padding: '6px', textAlign: 'center', borderBottom: '1px solid #eee' }}>{item.quantity || 0}</td>
                                                <td style={{ padding: '6px', textAlign: 'right', borderBottom: '1px solid #eee' }}>{formatCurrency(item.unit_price)}</td>
                                                <td style={{ padding: '6px', textAlign: 'right', borderBottom: '1px solid #eee' }}>{formatCurrency(item.amount_excl_vat || item.line_amount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {/* Totals */}
                                <div style={{ marginTop: '10px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', borderTop: '2px solid #898989' }}>
                                        <tbody>
                                            <tr>
                                                <td rowSpan={6} className="invoice_amount_in_words" style={{ width: '65%', verticalAlign: 'top', textAlign: 'left', padding: '4px 15px 4px 6px' }}>
                                                    {formatAmountInWords(data.net_total)}
                                                </td>
                                                <td style={{ padding: '4px 6px' }}>Gross Total:</td>
                                                <td style={{ padding: '4px 6px', textAlign: 'right' }}>{formatCurrency(data.gross_total)}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ padding: '4px 6px' }}>Discount %:</td>
                                                <td style={{ padding: '4px 6px', textAlign: 'right' }}>{data.discount_percentage || '0.00'}%</td>
                                            </tr>
                                            <tr>
                                                <td style={{ padding: '4px 6px' }}>Discount Amount:</td>
                                                <td style={{ padding: '4px 6px', textAlign: 'right' }}>{formatCurrency(data.discount_amount)}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ padding: '4px 6px' }}>Sub Total:</td>
                                                <td style={{ padding: '4px 6px', textAlign: 'right' }}>{formatCurrency(data.sub_total)}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ padding: '4px 6px' }}>VAT Amount:</td>
                                                <td style={{ padding: '4px 6px', textAlign: 'right' }}>{formatCurrency(data.vat_amount)}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ padding: '4px 6px', fontWeight: 700 }}>Net Total:</td>
                                                <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700 }}>{formatCurrency(data.net_total)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                {/* Footer */}
                                <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                                    <div>Page <span className="__page__">1</span> / <span className="__pages__">1</span></div>
                                    <div>Thank you for your business</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CashMemoDirectPreview;

